// /crm/leads/new — cadastro de lead (Story 11.2)
// Após criar, exibe avisos de dedup (FR-50.9) no toast — não bloqueia.

import { ArrowLeft } from 'lucide-react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { LeadForm } from '../../../components/crm/LeadForm';
import { useCreateCrmLead } from '../../../hooks/use-crm-leads';
import { useCrmPermissions } from '../../../hooks/use-crm-permissions';
import { useSaasProducts } from '../../../hooks/use-saas-products';
import { useUsers } from '../../../hooks/use-users';
import { extractErrorMessage } from '../../../lib/errors';

export function NewCrmLeadPage(): JSX.Element {
  const navigate = useNavigate();
  const { canWrite } = useCrmPermissions();
  const create = useCreateCrmLead();
  const { data: saasProducts } = useSaasProducts({ limit: 100 });
  const { data: responsibleUsers } = useUsers();

  // RBAC (FR-59.2 / AC3): read-only roles não acessam o fluxo de criação nem por
  // URL direta — o backend bloqueia o POST de qualquer forma, aqui é a UX.
  if (!canWrite) {
    return <Navigate to="/crm/leads" replace />;
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5 pb-10">
      <header className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => navigate('/crm/leads')}
          className="inline-flex w-fit items-center gap-1 text-[12px] text-muted transition-colors hover:text-ink"
        >
          <ArrowLeft size={13} strokeWidth={2} />
          Leads
        </button>
        <h1 className="m-0 text-[26px] font-bold leading-tight tracking-[-0.025em] text-ink">
          Novo lead
        </h1>
        <p className="text-[13px] text-muted">
          Cadastre o contato no funil. Só o nome é obrigatório — o resto você complementa depois.
        </p>
      </header>

      <LeadForm
        isPending={create.isPending}
        submitLabel="Cadastrar lead"
        saasProducts={saasProducts?.items ?? []}
        responsibleUsers={responsibleUsers ?? []}
        onCancel={() => navigate('/crm/leads')}
        onSubmit={async (values) => {
          try {
            const { lead, warnings } = await create.mutateAsync(values);
            if (warnings.length > 0) {
              toast.warning('Lead cadastrado — atenção a possível duplicata.', {
                description: `Já existe ${warnings.length === 1 ? 'um registro' : `${warnings.length} registros`} com o mesmo contato.`,
              });
            } else {
              toast.success('Lead cadastrado.', { description: lead.name });
            }
            navigate(`/crm/leads/${lead.id}`);
          } catch (err) {
            toast.error('Não conseguimos cadastrar.', {
              description: extractErrorMessage(err, 'Tente de novo em instantes.'),
              action: {
                label: 'Tentar de novo',
                onClick: () => void create.mutateAsync(values),
              },
            });
          }
        }}
      />
    </div>
  );
}
